import { type ReactNode } from "react";

import { sipSelectOptions } from "../lib/sipLabels";
import { trunkAllowsCountry, countryByIso } from "../lib/country";
import type { CallSetupController } from "../lib/useCallSetup";
import { Button, Check, MenuSelect, Select, TextArea, TextInput, cx } from "./ui";

const CID_KIND_TOGGLES = [
  ["optimal", "Optimal"],
  ["landline", "Landline"],
  ["mobile", "Mobile"],
  ["premium", "Premium"],
] as const;

function countryChoiceLabel(item: { iso2: string; calling_code: string; name: string }): string {
  const code = item.calling_code.replace(/\D/g, "");
  if (!code && !item.name.trim()) return item.iso2;
  return code ? `${item.iso2} · +${code} · ${item.name}` : `${item.iso2} · ${item.name}`;
}

function Prompt({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 font-mono text-[12px] text-mute">
        {label} <span className="text-teal">▸</span>
      </div>
      {children}
    </div>
  );
}

/**
 * One originate path for /call and the console drawer.
 * Dest, named profile, script/IVR, country/lang, gateway.
 */
export function OriginateFields({
  setup,
  onDesign,
}: {
  setup: CallSetupController;
  onDesign?: () => void;
}) {
  const { form, patch, busy } = setup;
  const scriptKnown = setup.scriptOptions.some((item) => item.value === setup.scriptValue);
  const otpGrey = !setup.scriptHasCollect;
  const languageCodes = setup.languages.map((item: { code: string }) => item.code);
  const sipCountries = setup.sipCountries;
  const effective = setup.effectiveCountry;
  const calling = countryByIso(effective, setup.countries)?.calling_code ?? "";
  const inferred = setup.inferredCountry;
  const inferredOffered = Boolean(inferred && sipCountries.some((item) => item.iso2 === inferred));
  const varChips = setup.catalogFeatures.vars;

  return (
    <>
      <Prompt label="profile">
        <Select
          data-field="profile"
          value={setup.profiles.includes(form.profile) ? form.profile : ""}
          disabled={busy}
          onChange={(event) => void setup.loadProfile(event.target.value)}
          onKeyDown={setup.onAdvance}
        >
          <option value="">{setup.profiles.length === 0 ? "No saved profiles" : "Select a profile…"}</option>
          {setup.profiles.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
        <div className="mt-2 flex gap-2">
          <TextInput
            value={form.profile}
            placeholder="Name to save"
            className="flex-1"
            onChange={(event) => patch({ profile: event.target.value })}
            onKeyDown={setup.onAdvance}
          />
          <Button variant="ghost" disabled={busy} onClick={() => void setup.loadProfile(form.profile)}>
            Load
          </Button>
        </div>
        <p className="mt-1 font-mono text-[11px] text-mute">Dest and CID optional when saving.</p>
      </Prompt>

      <div className="grid gap-3 md:grid-cols-2">
        <Prompt label="dest">
          <TextInput
            data-field="dest"
            value={form.dest}
            placeholder="+44…"
            autoComplete="off"
            onChange={(event) => patch({ dest: event.target.value })}
            onKeyDown={setup.onAdvance}
          />
        </Prompt>
        <Prompt label="name">
          <TextInput
            data-field="name"
            value={form.name}
            placeholder="Name they hear"
            onChange={(event) => patch({ name: event.target.value })}
            onKeyDown={setup.onAdvance}
          />
        </Prompt>
        <Prompt label="country">
          <Select
            data-field="country"
            value={sipCountries.some((item) => item.iso2 === form.country) ? form.country : ""}
            onChange={(event) => setup.setCountry(event.target.value)}
            onKeyDown={setup.onAdvance}
          >
            <option value="">{inferredOffered ? `Infer from dest · ${inferred}` : "Infer from dest"}</option>
            {sipCountries.map((item) => (
              <option key={item.iso2} value={item.iso2}>
                {countryChoiceLabel(item)}
              </option>
            ))}
          </Select>
          <p className="mt-1 font-mono text-[11px] text-mute">
            {effective ? `Using ${effective}` : "Country follows the number when the catalog can see it"}
          </p>
        </Prompt>
        <Prompt label="profile countries">
          {/* Remount so the adder returns to Any after a pick. */}
          <Select
            key={setup.profileCountries.join(",")}
            aria-label="profile countries"
            value=""
            onChange={(event) => setup.addProfileCountry(event.target.value)}
          >
            <option value="">{setup.profileCountries.length === 0 ? "Any" : "Add country…"}</option>
            {sipCountries
              .filter((item) => !setup.profileCountries.includes(item.iso2))
              .map((item) => (
                <option key={item.iso2} value={item.iso2}>
                  {countryChoiceLabel(item)}
                </option>
              ))}
          </Select>
          {setup.profileCountries.length ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {setup.profileCountries.map((iso) => (
                <button
                  key={iso}
                  type="button"
                  className="rounded-md border border-teal/20 px-2 py-1 font-mono text-[11px] text-teal"
                  aria-label={`Remove ${iso}`}
                  onClick={() => setup.removeProfileCountry(iso)}
                >
                  {iso} ×
                </button>
              ))}
            </div>
          ) : null}
          <p className="mt-1 font-mono text-[11px] text-mute">Saved on the profile. Empty does not restrict.</p>
        </Prompt>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Prompt label="cid">
          <Select
            aria-label="CID country"
            value={setup.cidCountry}
            onChange={(event) => setup.setCidCountry(event.target.value)}
          >
            {setup.cidCountry ? null : <option value="">Pick a country</option>}
            {sipCountries.map((item) => (
              <option key={item.iso2} value={item.iso2}>
                {countryChoiceLabel(item)}
              </option>
            ))}
          </Select>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <TextInput
              data-field="cid"
              value={form.cid}
              placeholder="Owned CLI"
              className="min-w-[9rem] flex-1"
              onChange={(event) => patch({ cid: event.target.value })}
              onKeyDown={setup.onAdvance}
            />
            <div className="flex flex-wrap items-center gap-1">
              <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="CID class">
                {CID_KIND_TOGGLES.map(([value, label]) => {
                  const on = setup.cidKind === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={!setup.cidKindEnabled(value)}
                      className={cx(
                        "rounded-md border px-2 py-1 font-mono text-[11px] disabled:cursor-not-allowed disabled:border-border disabled:text-mute",
                        on ? "border-teal/40 bg-teal/10 text-teal" : "border-border text-ink",
                      )}
                      onClick={() => setup.setCidKind(value)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="primary"
                className="min-h-11 shrink-0"
                onClick={() => void setup.randomCid(setup.cidCountry)}
              >
                Random CID
              </Button>
            </div>
          </div>
          <p className="mt-1 font-mono text-[11px] text-mute">
            Optimal uses the dearest prefix for this SIP when the sheet has one.
          </p>
        </Prompt>
        <Prompt label="sip">
          <MenuSelect
            data-field="sip"
            aria-label="sip"
            value={form.sip}
            onChange={(next) => setup.setSip(next)}
            options={sipSelectOptions(setup.sips).map((option) => {
              if (!option.value || !effective) return option;
              const row = setup.sips.find((item) => item.sofia_gateway_id === option.value);
              if (!row || trunkAllowsCountry(row.allowed_countries, effective, calling)) return option;
              return { ...option, label: `${option.label} · not for ${effective}` };
            })}
          />
        </Prompt>
        <Prompt label="speak as">
          <TextInput
            data-field="speak_as"
            value={form.speak_as}
            placeholder="Operator name"
            onChange={(event) => patch({ speak_as: event.target.value })}
            onKeyDown={setup.onAdvance}
          />
        </Prompt>
        <Prompt label="business">
          <TextInput
            data-field="business"
            value={form.business}
            placeholder="Business name"
            onChange={(event) => patch({ business: event.target.value })}
            onKeyDown={setup.onAdvance}
          />
        </Prompt>
        <Prompt label="purpose">
          <TextInput
            data-field="purpose"
            value={form.purpose}
            placeholder="account verification"
            onChange={(event) => patch({ purpose: event.target.value })}
            onKeyDown={setup.onAdvance}
          />
        </Prompt>
        <Prompt label="language">
          <Select
            data-field="language"
            value={setup.languageTouched ? form.language : ""}
            onChange={(event) => setup.setLanguage(event.target.value)}
            onKeyDown={setup.onAdvance}
          >
            <option value="">From the number</option>
            {setup.languages.map((item: { code: string; label?: string }) => (
              <option key={item.code} value={item.code}>
                {item.label}
              </option>
            ))}
          </Select>
          {setup.languageTouched && !languageCodes.includes(form.language) ? (
            <p className="mt-1 text-xs text-fail">Unknown language code</p>
          ) : null}
          <p className="mt-1 font-mono text-[11px] text-mute">
            {setup.willSpeak
              ? `Will speak ${
                  setup.languages.find((item: { code: string; label?: string }) => item.code === setup.willSpeak)
                    ?.label || setup.willSpeak
                }`
              : "Language follows the number"}
          </p>
          {setup.suggestLang &&
          languageCodes.includes(setup.suggestLang) &&
          setup.suggestLang !== form.language ? (
            <button
              type="button"
              className="mt-1 font-mono text-[11px] text-teal"
              onClick={() => setup.setLanguage(setup.suggestLang)}
            >
              Use {setup.suggestLang}
            </button>
          ) : null}
        </Prompt>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Prompt label="OTP length (1-12)">
          <Select
            data-field="digits"
            value={form.digits}
            disabled={busy || otpGrey}
            onChange={(event) => patch({ digits: event.target.value })}
            onKeyDown={setup.onAdvance}
          >
            {Array.from({ length: 12 }, (_, index) => {
              const n = String(index + 1);
              return (
                <option key={n} value={n}>
                  {n}
                </option>
              );
            })}
          </Select>
          <p className="mt-1 font-mono text-[11px] text-mute">
            {otpGrey ? "No capture / DTMF / gate in this script" : "How many digits the customer reads"}
          </p>
        </Prompt>
        <Prompt label="known otp">
          <TextInput
            data-field="known_otp"
            value={form.known_otp}
            placeholder="Optional — never invented"
            disabled={busy || otpGrey}
            onChange={(event) => patch({ known_otp: event.target.value })}
            onKeyDown={setup.onAdvance}
          />
        </Prompt>
      </div>

      <Prompt label="Script">
        <div className="flex gap-2">
          <Select
            data-field="script"
            className="flex-1"
            value={setup.scriptValue}
            onChange={(event) => setup.setScript(event.target.value)}
            onKeyDown={setup.onAdvance}
          >
            {!scriptKnown ? <option value={setup.scriptValue}>{form.ivr_id || form.pack}</option> : null}
            {setup.scriptOptions.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </Select>
          {onDesign ? (
            <Button variant="success" className="min-h-11 shrink-0" onClick={onDesign}>
              Design
            </Button>
          ) : null}
        </div>
        {setup.catalogFeatures.countryLang.length ? (
          <p className="mt-1 font-mono text-[11px] text-mute">
            Catalog branches: {setup.catalogFeatures.countryLang.join(", ")}
          </p>
        ) : (
          <p className="mt-1 font-mono text-[11px] text-mute">Office pack if the script is empty.</p>
        )}
      </Prompt>

      <Prompt label="line">
        <Select value={form.stage} disabled={busy || otpGrey} onChange={(event) => setup.setStage(event.target.value)}>
          {setup.stages.map((stage) => (
            <option key={stage} value={stage}>
              {stage}
            </option>
          ))}
        </Select>
        <TextArea
          data-field="line"
          className="mt-2"
          value={form.line}
          placeholder="Existing pack line loads here — edit to override for this call"
          disabled={busy || otpGrey}
          onChange={(event) => setup.setLine(event.target.value)}
        />
        {varChips.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {varChips.map((token) => (
              <button
                key={token}
                type="button"
                className="rounded-md border border-teal/20 px-2 py-1 font-mono text-[11px] text-teal"
                onClick={() => setup.insertVar(token)}
              >
                {`{{${token}}}`}
              </button>
            ))}
          </div>
        ) : null}
      </Prompt>

      {Object.keys(form.script_vars).length ? (
        <Prompt label="script vars">
          <ul className="space-y-1 font-mono text-[12px] text-mute">
            {Object.entries(form.script_vars).map(([key, value]) => (
              <li key={key} className="flex items-center gap-2">
                <span className="text-ink">{key}</span>
                <TextInput
                  value={value}
                  className="flex-1"
                  onChange={(event) => setup.setScriptVar(key, event.target.value)}
                />
                <button type="button" className="text-fail" onClick={() => setup.removeScriptVar(key)}>
                  remove
                </button>
              </li>
            ))}
          </ul>
        </Prompt>
      ) : null}

      <div className="flex flex-wrap gap-6">
        <Check label="replay" checked={form.identity_replay} onChange={(identity_replay) => patch({ identity_replay })} />
        <Check label="avmd" checked={form.avmd} onChange={(avmd) => patch({ avmd })} />
        <Check label="record" checked={form.record} onChange={(record) => patch({ record })} />
      </div>
    </>
  );
}
